import { render, hydrate } from 'preact';
import { Provider } from 'react-redux';
import { Frontload } from 'react-frontload';
import { Router } from "react-router";
import { helpers } from "@cargo/common";
import { createBrowserHistory } from 'history';
import _ from 'lodash';
import axios from 'axios';

import createStore from "./store";
import { actions, actionTypes } from "./actions";
import ErrorBoundary from "./errorBoundary";

import App from "./components/app";
import windowInfo from "./components/window-info"

import "./middleware/axiosDefaults";
import "./middleware/axiosCache";
import "./middleware/axiosAuth";

export * from './ssr';

// conditionally load the admin API so admin can hook into frontend
if(!helpers.isServer && helpers.isAdminEdit) {
	require('./adminAPI');
}

const { store } = createStore();

let history;

if(!helpers.isServer){

	history = createBrowserHistory();

	// attach our store to the windowInfo instance
	windowInfo.setupStore(store);

	let storedAuth;

	try {
		storedAuth = localStorage.getItem('c3-auth');
	} catch(e) {
		console.log('unable to retrieve stored auth', e);
	}

	const searchParams = new URLSearchParams(window.location.search);

	if(searchParams.has('auth')) {

		// Auth got passed as a query string, use this. 
		try {
			
			store.dispatch({
				type: actionTypes.AUTHENTICATE_USER_FULFILLED,
				payload: {
					authenticated: true,
					data: JSON.parse(searchParams.get('auth'))
				}
			});

			// delete queryString
			searchParams.delete('auth');
			
			let newSearchParams = searchParams.toString();

			if(newSearchParams.length > 0) {
				newSearchParams = '?' + newSearchParams;
			}

			// remove from URL without reloading page
			window.history.replaceState({}, document.title, window.location.origin + window.location.pathname + newSearchParams);

		} catch(e) {
			console.error('unable to use querystring auth', e);
		}

	} else if(storedAuth) {

		try {
			store.dispatch({
				type: actionTypes.AUTHENTICATE_USER_FULFILLED,
				payload: {
					authenticated: true,
					data: JSON.parse(storedAuth)
				}
			});
		} catch(e) {
			console.error('unable to retrieve stored auth', e);
		}
		
	}

	// prevent auto scroll restoration by the browser
	if (window.history.scrollRestoration) {
		window.history.scrollRestoration = 'manual';
	}

	// Scroll restoration for page navigation
	const oldScrollPositions = {};

	// scroll to top when navigating (not on back button)
	history.push = _.wrap(history.push, function(original) {

		// store scroll position of currently rendered content
		oldScrollPositions[window.location.pathname] = document.scrollingElement.scrollTop;

		// call the orig function
		var args = Array.prototype.slice.call(arguments);

		if (args.some(opt => opt.preventScrollReset === true )) {
			/* Do not reset page scroll */
		} else {

			const currentPID = store.getState().frontendState.activePID;
			const unsubscribe = store.subscribe(() => {

				// check if new active PID is different
				if(store.getState().frontendState.activePID !== currentPID) {

					// new content was rendered. Scroll to top
					document.scrollingElement.scrollTo(0,0);
					// stop listening
					unsubscribe();

				}

				setTimeout(() => {
					// don't restore scroll after 1 sec
					unsubscribe();
				}, 1000)

			});

		}

		args.shift();

		original.apply(this, args);

	});

	// back/forward buttons
	window.addEventListener('popstate', e => {

		const newPathname = window.location.pathname;
		const oldPathName = history.location.pathname;

		// update scroll position of currently rendered content
		oldScrollPositions[oldPathName] = document.scrollingElement.scrollTop;

		// React renders are async. Listen for active PID change
		// to make sure we have the required content rendered before scrolling down
		const currentPID = store.getState().frontendState.activePID;

		// listen to store changes
		const unsubscribe = store.subscribe(() => {

			// check if new active PID is different
			if(
				store.getState().frontendState.activePID !== currentPID
				&& oldScrollPositions[newPathname]
			) {

				// console.log('Attempting to restore scroll pos for', newPathname, 'to', oldScrollPositions[newPathname])

				const pidBeingScrolled = store.getState().frontendState.activePID;

				setTimeout(async () => {
				
					// reset
					let scrollAttempts = 0;
					let tryScrolling = true;

					while(
						// keep trying till we scrolled
						tryScrolling
						// make sure we only scroll when we're still rendering the same PID
						&& store.getState().frontendState.activePID === pidBeingScrolled
					) {

						if(scrollAttempts > 10) {
							tryScrolling = false
						}

						// only scroll 
						if(document.scrollingElement.scrollHeight >= oldScrollPositions[newPathname]) {
							document.scrollingElement.scrollTop = oldScrollPositions[newPathname];
							// console.log('restored scroll position', oldScrollPositions[newPathname])
							tryScrolling = false;
						}

						scrollAttempts++;

						// wait 100ms till we try again
						await new Promise(resolve => setTimeout(resolve, 100));

					}
				})

				// stop listening
				unsubscribe();

			}

		});

	});

	// Running locally, we should run on a <ConnectedRouter /> rather than on a <StaticRouter /> like on the server
	// Let's also let React Frontload explicitly know we're not rendering on the server here
	const Application = (
		<Provider store={store}>
			<Router history={history}>
				<Frontload noServerRender={true}>
					<App />
				</Frontload>
			</Router>
		</Provider>
	);
	
	const root = document.body;

	if (root.querySelector('.content') !== null) {

		// Callback function to execute when mutations are observed
// 		const callback = (mutationList, observer) => {
// 			for (const mutation of mutationList) {
// 				mutation.addedNodes.forEach(node => {
// 					console.log('hydrate debug: added', node);
// 				})
// 				mutation.removedNodes.forEach(node => {
// 					console.log('hydrate debug: removed', node);
// 				})
// 			}
// 		};
// 
// 		const observer = new MutationObserver(callback);
// 
// 		observer.observe(root, { attributes: true, childList: true, subtree: true });

		hydrate(Application, root);

		// requestAnimationFrame(() => {
		// 	console.log(observer.takeRecords());
		// 	observer.disconnect();
		// })

	} else {
		
		render(Application, root);

	}

	// show body after first render/hydrate
	requestAnimationFrame(() => {
		document.body.style.opacity = '';
	})

}

export { store, history };